import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseMutationOptions } from '@tanstack/react-query';
import { supabase } from '../utils/supabaseClient';

export interface RunQcContactsRequest {
  program_id: string;
  baci_code: string;
  seed_url: string;
  organization?: string;
}

export interface RunQcContactsResponse {
  status: 'completed' | 'failed';
  message: string;
  report_path?: string;
  error?: string;
  agent_response?: string;
}

/**
 * Invoke the qc-run-contacts Edge Function to trigger automated contact verification
 */
async function runQcContactsMutation(
  request: RunQcContactsRequest
): Promise<RunQcContactsResponse> {
  if (!request.program_id) {
    throw new Error('program_id is required');
  }

  if (!request.baci_code) {
    throw new Error('baci_code is required');
  }

  if (!request.seed_url) {
    throw new Error('seed_url is required');
  }

  console.log('[QC Contacts] Invoking edge function with request:', {
    program_id: request.program_id,
    baci_code: request.baci_code,
    has_seed_url: !!request.seed_url,
    seed_url_preview: request.seed_url?.substring(0, 50)
  });

  const { data, error } = await supabase.functions.invoke<RunQcContactsResponse>(
    'qc-run-contacts',
    {
      body: request
    }
  );

  console.log('[QC Contacts] Response:', { data, error });

  if (error) {
    const errorDetails = {
      message: error.message,
      name: error.name,
      context: (error as any).context,
      status: (error as any).status
    };
    console.error('[QC Contacts] Edge function error:', errorDetails);

    // Try to extract response body for better error details
    if ((error as any).context instanceof Response) {
      try {
        const responseBody = await (error as any).context.clone().text();
        console.error('[QC Contacts] Response body:', responseBody);
        throw new Error(`Edge Function Error: ${error.message}\n\nResponse: ${responseBody}`);
      } catch (readError) {
        console.error('[QC Contacts] Could not read response body:', readError);
      }
    }

    throw new Error(`Edge Function Error: ${error.message || 'Failed to invoke QC contacts verification'}`);
  }

  if (!data) {
    throw new Error('No response from QC contacts verification');
  }

  if (data.status === 'failed') {
    throw new Error(data.error || data.message || 'QC verification failed');
  }

  return data;
}

/**
 * React Query mutation hook to run QC contacts verification
 * Automatically refetches contact data after successful verification
 */
export function useRunQcContacts(
  options?: UseMutationOptions<RunQcContactsResponse, Error, RunQcContactsRequest>
): UseMutationResult<RunQcContactsResponse, Error, RunQcContactsRequest> {
  const queryClient = useQueryClient();

  return useMutation<RunQcContactsResponse, Error, RunQcContactsRequest>({
    mutationFn: runQcContactsMutation,
    onSuccess: (_data, variables) => {
      // Invalidate and refetch the contacts query for this program
      queryClient.invalidateQueries({
        queryKey: ['program-contacts', variables.program_id]
      });

      // Also invalidate activity logs
      queryClient.invalidateQueries({
        queryKey: ['activity-log', variables.program_id]
      });
    },
    ...options
  });
}

export default useRunQcContacts;
